import React, { useCallback, useEffect, useRef, useState } from "react";
import { SplitView } from "@/components/ui";
import { Review, ReviewReplyStatus, ReviewServices } from "@/services/bedrock";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ReviewListFiltersBar } from "@/components/reviews";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { CoachReviewCard } from "../../components/CoachReviewCard";
import { ReviewDetailPanel } from "./ReviewDetailPanel";
import { ReviewsFiltersPopover } from "./ReviewsFiltersPopover";
import { useCoachReviewsFilters } from "./CoachReviewsFiltersContext";
import type { CoachReviewsInfiniteQueryResult } from "./useCoachReviewsInfiniteQuery";
type ReviewsSplitViewProps = {
  reviewsQuery: CoachReviewsInfiniteQueryResult;
  selectedReviewId: string;
  onSelectedReviewIdChange: (reviewId: string) => void;
  onClose: () => void;
};

export function ReviewsSplitView({
  reviewsQuery,
  selectedReviewId,
  onSelectedReviewIdChange,
  onClose,
}: ReviewsSplitViewProps) {
  const queryClient = useQueryClient();
  const { filters, searchInput, setSearchInput, activeFilterCount } = useCoachReviewsFilters();

  const [filterOpen, setFilterOpen] = useState(false);
  const [pendingSelectNext, setPendingSelectNext] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const {
    reviews,
    totalCount,
    filterParams,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isRefreshing,
    isPlaceholderData,
    isError,
    refetch,
  } = reviewsQuery;

  const isReviewSelected = useCallback(
    (reviewId: string | number) => String(reviewId) === String(selectedReviewId),
    [selectedReviewId]
  );
  const selectedFromList = reviews.find(review => isReviewSelected(review.id));

  const { data: fetchedReview } = useQuery({
    queryKey: ["review", selectedReviewId],
    queryFn: () => ReviewServices.getReview(selectedReviewId),
    enabled: !selectedFromList,
  });

  const selectedReview = selectedFromList ?? fetchedReview ?? null;

  const filterKey = JSON.stringify(filterParams);
  const prevFilterKeyRef = useRef(filterKey);
  const reselectAfterFilterChangeRef = useRef(false);
  if (prevFilterKeyRef.current !== filterKey) {
    prevFilterKeyRef.current = filterKey;
    reselectAfterFilterChangeRef.current = true;
  }

  useEffect(() => {
    if (!reviews.length || isPlaceholderData) return;

    const stillVisible = reviews.some(review => isReviewSelected(review.id));
    if (stillVisible) {
      reselectAfterFilterChangeRef.current = false;
      return;
    }
    if (!reselectAfterFilterChangeRef.current) return;

    reselectAfterFilterChangeRef.current = false;
    onSelectedReviewIdChange(String(reviews[0].id));
  }, [reviews, isReviewSelected, onSelectedReviewIdChange, isPlaceholderData]);

  // After loading the next page for keyboard "next", select the newly available review.
  useEffect(() => {
    if (!pendingSelectNext || isFetchingNextPage || isPlaceholderData) return;

    const selectedIndex = reviews.findIndex(review => isReviewSelected(review.id));
    if (selectedIndex >= 0 && selectedIndex < reviews.length - 1) {
      onSelectedReviewIdChange(String(reviews[selectedIndex + 1].id));
    }
    setPendingSelectNext(false);
  }, [pendingSelectNext, isFetchingNextPage, isPlaceholderData, reviews, isReviewSelected, onSelectedReviewIdChange]);

  const replyStatusMutation = useMutation({
    mutationFn: ({ reviewId, replyStatus }: { reviewId: string; replyStatus: ReviewReplyStatus }) =>
      ReviewServices.updateReview(reviewId, { replyStatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      queryClient.invalidateQueries({ queryKey: ["review-snapshot"] });
      queryClient.invalidateQueries({ queryKey: ["review-details"] });
      queryClient.invalidateQueries({ queryKey: ["review", selectedReviewId] });
    },
    onError: () => {
      toast.error("Error updating reply status. Please try again.");
    },
  });

  const selectReview = useCallback(
    (review: Review) => {
      onSelectedReviewIdChange(String(review.id));
    },
    [onSelectedReviewIdChange]
  );

  const selectedIndex = reviews.findIndex(review => isReviewSelected(review.id));
  const selectedInList = selectedIndex >= 0;
  const hasPrevious = selectedInList && selectedIndex > 0;
  const hasNextInList = selectedInList && selectedIndex < reviews.length - 1;
  const canGoPrevious = !isFetchingNextPage && hasPrevious;
  const canGoNext = !isFetchingNextPage && selectedInList && (hasNextInList || Boolean(hasNextPage));

  const goToPreviousReview = useCallback(() => {
    if (!hasPrevious) return;
    selectReview(reviews[selectedIndex - 1]);
  }, [hasPrevious, reviews, selectedIndex, selectReview]);

  const goToNextReview = useCallback(() => {
    if (!selectedInList) return;
    if (hasNextInList) {
      selectReview(reviews[selectedIndex + 1]);
      return;
    }
    if (!hasNextPage || isFetchingNextPage) return;
    setPendingSelectNext(true);
    void fetchNextPage();
  }, [
    selectedInList,
    hasNextInList,
    hasNextPage,
    isFetchingNextPage,
    reviews,
    selectedIndex,
    selectReview,
    fetchNextPage,
  ]);

  useEffect(() => {
    if (!hasNextPage) return;

    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: "200px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, reviews.length]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(
        `[data-review-list-item="${CSS.escape(String(selectedReviewId))}"]`
      );
      el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });

    return () => cancelAnimationFrame(frame);
  }, [selectedReviewId]);

  useEffect(() => {
    const isTypingTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target.isContentEditable ||
        Boolean(target.closest('[contenteditable="true"]'))
      );
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (isTypingTarget(event.target)) return;

      if ((event.key === "ArrowLeft" || event.key === "ArrowUp") && canGoPrevious) {
        event.preventDefault();
        goToPreviousReview();
        return;
      }

      if ((event.key === "ArrowRight" || event.key === "ArrowDown") && canGoNext) {
        event.preventDefault();
        goToNextReview();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canGoPrevious, canGoNext, goToPreviousReview, goToNextReview]);

  const listHeader = (
    <div className="bg-transparent p-4">
      <ReviewListFiltersBar
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        activeFilterCount={activeFilterCount}
        filterOpen={filterOpen}
        onFilterOpenChange={setFilterOpen}
        filterContent={<ReviewsFiltersPopover onClose={() => setFilterOpen(false)} resultCount={totalCount ?? null} />}
      />
    </div>
  );

  const isListBusy = isRefreshing || (isLoading && reviews.length === 0);

  return (
    <SplitView className={cn("h-[calc(100dvh-100px)] min-h-180", isListBusy && "opacity-60")}>
      <SplitView.List header={listHeader} bodyClassName="pt-3">
        <div className="px-4 pb-4 flex flex-col gap-3">
          {reviews.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              {isError ? (
                <div className="flex flex-col items-center gap-3">
                  <span>Couldn't load reviews.</span>
                  <button
                    type="button"
                    className="text-sm font-medium text-primary hover:underline"
                    onClick={() => void refetch()}
                  >
                    Retry
                  </button>
                </div>
              ) : (
                "No reviews match the current filters."
              )}
            </div>
          ) : (
            reviews.map(review => (
              <div key={review.id} data-review-list-item={String(review.id)}>
                <CoachReviewCard
                  review={review}
                  isSelected={isReviewSelected(review.id)}
                  primaryRatingType={filters.ratingType}
                  onClick={() => selectReview(review)}
                />
              </div>
            ))
          )}

          {hasNextPage ? (
            <div
              ref={loadMoreRef}
              className="py-4 flex items-center justify-center gap-2 text-muted-foreground shrink-0"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", isFetchingNextPage && "animate-spin")} />
              <span className="text-[10px] font-bold uppercase tracking-widest">
                {isFetchingNextPage ? "Loading more reviews..." : "Scroll to load more"}
              </span>
            </div>
          ) : null}
        </div>
      </SplitView.List>

      {selectedReview ? (
        <ReviewDetailPanel
          review={selectedReview}
          onUpdateReplyStatus={replyStatus => replyStatusMutation.mutate({ reviewId: selectedReview.id, replyStatus })}
          isUpdatingReplyStatus={replyStatusMutation.isPending}
          onPreviousReview={canGoPrevious ? goToPreviousReview : undefined}
          onNextReview={canGoNext ? goToNextReview : undefined}
          onClose={onClose}
        />
      ) : (
        <SplitView.Detail bodyClassName="pt-2" onClose={onClose}>
          <div className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground text-center">
            No review selected. Adjust filters to find reviews.
          </div>
        </SplitView.Detail>
      )}
    </SplitView>
  );
}
